<template>
  <div v-if="loading" class="p-8 text-center">
    <Loader />
  </div>

  <ValidationObserver v-else slim v-slot="{ handleSubmit, invalid }" ref="observer">
    <form @submit.prevent="handleSubmit(submit)">
      <BaseLayoutSpaced relaxed>
        <ValidationProvider
          v-for="field in formFieldsWithDefaults"
          :key="field.name"
          :name="field.name"
          :vid="field.name"
          :rules="field.validation"
          v-slot="{ errors, failed }"
          slim
        >
          <BaseFormGroup>
            <BaseFormLabel :for="`${_uid}-${field.name}`">
              {{ field.label }} <template v-if="field.validation.required"> * </template>
            </BaseFormLabel>
            <Component
              v-model="data[field.name]"
              :is="field.component"
              :disabled="submitting"
              v-bind="{
                ...field.options.props,
                ...field.options.attrs,
              }"
              :invalid="failed"
              :id="`${_uid}-${field.name}`"
            />
            <BaseFormInlineMessage v-if="failed" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </ValidationProvider>

        <BaseFormInlineMessage v-if="message" danger>
          {{ message }}
        </BaseFormInlineMessage>

        <div>
          <BaseButton primary type="submit" :disabled="invalid || submitting">
            <span v-if="!submitting">Submit</span>
            <Loader v-else small inverse />
          </BaseButton>
          <BaseButton
            ghost
            type="button"
            :disabled="submitting"
            class="ml-2"
            @click="$emit('cancel')"
          >
            Cancel
          </BaseButton>
        </div>
      </BaseLayoutSpaced>
    </form>
  </ValidationObserver>
</template>

<script>
import { mapActions } from 'vuex';
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';
import { HTTP_UNPROCESSABLE_ENTITY, HTTP_OK } from '@/api/http-response-codes';
import { createProductServiceUpgradeApi, editProductServiceUpgradeApi } from '@/api/clients/admin';

const defaultProductService = {
  upgradesProductServiceUuid: null,
  serviceUuid: null,
  name: '',
  price: 0,
  requirements: null,
};

const defaultField = {
  component: null,
  label: '',
  name: '',
  options: {},
  validation: {},
};

export default {
  name: 'FormProductServiceUpgrade',
  mixins: [veeValidateErrorsMixin],
  props: {
    productServiceUuid: {
      type: String,
      required: true,
    },
    productServiceUpgradeUuid: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    submitting: false,
    loading: false,
    data: {
      ...defaultProductService,
    },
    productService: null,
    services: [],
    message: null,
  }),
  computed: {
    formFields() {
      return [
        {
          component: 'BaseFormSelect',
          label: 'Upgrade service',
          name: 'serviceUuid',
          options: {
            attrs: {
              name: 'serviceUuid',
            },
            props: {
              options: this.services,
            },
          },
          validation: {
            required: true,
          },
        },
        {
          component: 'BaseFormInput',
          label: 'Name',
          name: 'name',
          options: {
            attrs: {
              type: 'text',
              name: 'name',
              class: 'w-full',
            },
          },
          validation: {
            required: true,
            min: 5,
            max: 255,
          },
        },
        {
          component: 'BaseFormInput',
          label: 'Price',
          name: 'price',
          options: {
            attrs: {
              type: 'number',
              name: 'price',
              step: '0.01',
            },
          },
          validation: {
            required: true,
            min_value: this.productService.price,
          },
        },
        {
          component: 'BaseFormTextarea',
          label: 'Requirements',
          name: 'requirements',
          options: {
            attrs: {
              name: 'requirements',
            },
          },
          validation: {
            //
          },
        },
      ];
    },
    formFieldsWithDefaults() {
      return this.formFields.map((x) => ({ ...defaultField, ...x }));
    },
  },
  watch: {
    'data.serviceUuid': {
      handler(value) {
        if (!value) return;

        const { label = '', price } = this.services.find((x) => x.value === value) || {};

        if (!this.data.name) {
          this.data.name = label;
        }

        if (!this.data.price) {
          this.data.price = price;
        }
      },
    },
  },
  created() {
    if (this.productServiceUpgradeUuid) {
      this.edit();
    } else {
      this.create();
    }
  },
  methods: {
    ...mapActions('admin/productServiceUpgrades', [
      'storeProductServiceUpgrade',
      'updateProductServiceUpgrade',
    ]),
    async create() {
      this.loading = true;

      try {
        const { data = {}, status } = await createProductServiceUpgradeApi(this.productServiceUuid);

        if (status && status === HTTP_OK) {
          this.productService = data.data;
          this.services = data.services;
          this.data.price = data.data.price;
          this.data.upgradesProductServiceUuid = this.productServiceUuid;
        }
      } catch (e) {
        //
      }

      this.loading = false;
    },
    async edit() {
      this.loading = true;

      try {
        const { data = {}, status } = await editProductServiceUpgradeApi(
          this.productServiceUpgradeUuid
        );

        if (status && status === HTTP_OK) {
          Object.assign(this.data, {}, data.data);
          this.productService = data.productService;
          this.services = data.services;
        }
      } catch (e) {
        //
      }

      this.loading = false;
    },
    async submit() {
      if (this.productServiceUpgradeUuid) {
        this.update();
      } else {
        this.store();
      }
    },
    async store() {
      this.submitting = true;

      const { uuid: productServiceUuid } = this.productService;

      const submitWithErrorHandling = this.handleFormErrors(this.storeProductServiceUpgrade);

      await submitWithErrorHandling({
        productServiceUuid,
        params: this.data,
      });

      this.submitting = false;
    },
    async update() {
      const { productServiceUpgradeUuid } = this;

      this.submitting = true;

      const submitWithErrorHandling = this.handleFormErrors(this.updateProductServiceUpgrade);

      await submitWithErrorHandling({ productServiceUpgradeUuid, params: this.data });

      this.submitting = false;
    },
    handleFormErrors(fn) {
      return (...params) =>
        fn(...params)
          .then(() => {
            this.$emit('success');
          })
          .catch(({ status, data } = {}) => {
            // do sth with the error!
            if (status && status === HTTP_UNPROCESSABLE_ENTITY) {
              this.observer = this.$refs.observer;
              this.addServerErrors(data.errors);
            }
          });
    },
  },
};
</script>
